// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { produce } from 'immer';
import { isDefined } from '@shared/utils';
import { type AccountsTransactionsData } from '../../interfaces/api/brokerage/brokerage';

/**
 * Gets the exchange rate for a currency, defaulting to 1 if not found.
 *
 * @param currency - The currency code.
 * @param exchangeRates - A record of exchange rates.
 * @returns The exchange rate for the currency or 1 if not found.
 */
const getExchangeRate = (
  currency: string | null | undefined,
  exchangeRates: Record<string, number>,
): number => {
  return currency ? exchangeRates[currency] ?? 1 : 1;
};

/**
 * Converts a numeric value using the provided exchange rate if the value is not
 * null.
 *
 * @param value - The numeric value to convert (can be null or undefined).
 * @param rate - The exchange rate to apply.
 * @returns The converted value or null if the original value was null/undefined.
 */
const convertValue = (
  value: number | null | undefined,
  rate: number,
): number | null => {
  return isDefined(value) ? value * rate : null;
};

/**
 * Converts currency values for a transaction.
 *
 * @param transaction - The transaction to convert.
 * @param exchangeRates - A record of exchange rates.
 */
const convertTransaction = (
  transaction: AccountsTransactionsData['transactions'][number],
  exchangeRates: Record<string, number>,
): void => {
  const rate = getExchangeRate(transaction.currency, exchangeRates);
  transaction.amount = convertValue(transaction.amount, rate);
  transaction.price = convertValue(transaction.price, rate);
};

/**
 * Converts currency values for an account's transactions.
 *
 * @param account - The account to convert.
 * @param exchangeRates - A record of exchange rates.
 */
const convertAccount = (
  account: AccountsTransactionsData,
  exchangeRates: Record<string, number>,
): void => {
  for (const transaction of account.transactions) {
    convertTransaction(transaction, exchangeRates);
  }
};

/**
 * Converts the currency of accounts transactions based on provided exchange rates.
 *
 * @param accountsTransactions - An array of account transactions to be converted.
 * @param exchangeRates - A record of exchange rates where the key is the currency
 *   code and the value is the exchange rate.
 * @returns An array of account transactions with converted currency values using
 *   immer for immutable updates.
 */
export const convertAccountsTransactionsCurrency = (
  accountsTransactions: AccountsTransactionsData[],
  exchangeRates: Record<string, number>,
): AccountsTransactionsData[] => {
  if (accountsTransactions.length === 0) {
    return [];
  }

  return produce(accountsTransactions, draft => {
    for (const account of draft) {
      convertAccount(account, exchangeRates);
    }
  });
};
